import { type MetaFunction } from '@remix-run/node';
import { Link } from '@remix-run/react';
import { useState } from 'react';
import Header from '~/components/Header';
import Footer from '~/components/Footer';
import FAQ from '~/components/FAQ';
import { generateCanonicalUrl, SITE_NAME } from '~/utils/seo';
import {
  ArrowRightIcon,
  CheckIcon,
  ClipboardIcon,
  CommandLineIcon,
  CpuChipIcon,
  PuzzlePieceIcon,
  CodeBracketIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { SiClaude, SiOpenai } from 'react-icons/si';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import {
  FaTiktok,
  FaInstagram,
  FaYoutube,
  FaFacebook,
  FaXTwitter,
  FaLinkedin,
  FaPinterest,
  FaThreads,
  FaBluesky,
} from 'react-icons/fa6';

export const maxDuration = 300;

export const meta: MetaFunction = () => {
  const canonicalUrl = generateCanonicalUrl('/agents');
  const title = `${SITE_NAME} for AI Agents: MCP Server & Social Media API`;
  const description =
    'Hand your social media to an AI agent. Connect Claude, Cursor, or Codex to TheTabber and let it draft, schedule, and publish across 9 platforms via MCP, a skill, or REST.';

  return [
    { title },
    { name: 'description', content: description },
    {
      name: 'keywords',
      content:
        'social media MCP server, AI agent social media API, Claude social media, post to social media with AI, MCP, model context protocol, social media automation API',
    },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:type', content: 'website' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
  ];
};

function CursorLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 466.73 532.09" className={className} fill="currentColor" aria-hidden="true">
      <path d="M457.43,125.94L244.42,2.96c-6.84-3.95-15.28-3.95-22.12,0L9.3,125.94c-5.75,3.32-9.3,9.46-9.3,16.11v247.99c0,6.65,3.55,12.79,9.3,16.11l213.01,122.98c6.84,3.95,15.28,3.95,22.12,0l213.01-122.98c5.75-3.32,9.3-9.46,9.3-16.11v-247.99c0-6.65-3.55-12.79-9.3-16.11h-.01ZM444.05,151.99l-205.63,356.16c-1.39,2.4-5.06,1.42-5.06-1.36v-233.21c0-4.66-2.49-8.97-6.53-11.31L24.87,145.67c-2.4-1.39-1.42-5.06,1.36-5.06h411.26c5.84,0,9.49,6.33,6.57,11.39h-.01Z" />
    </svg>
  );
}

const platforms = [
  { name: 'TikTok', icon: FaTiktok, color: '#000000' },
  { name: 'Instagram', icon: FaInstagram, color: '#E4405F' },
  { name: 'YouTube', icon: FaYoutube, color: '#FF0000' },
  { name: 'Facebook', icon: FaFacebook, color: '#1877F2' },
  { name: 'X (Twitter)', icon: FaXTwitter, color: '#000000' },
  { name: 'LinkedIn', icon: FaLinkedin, color: '#0A66C2' },
  { name: 'Pinterest', icon: FaPinterest, color: '#BD081C' },
  { name: 'Threads', icon: FaThreads, color: '#000000' },
  { name: 'Bluesky', icon: FaBluesky, color: '#0085FF' },
];

const MCP_CLAUDE_CODE = `claude mcp add thetabber --scope user \\
  --env TABBER_API_KEY=ttbr_live_… \\
  -- npx -y @thetabber/mcp`;

const MCP_JSON = `{
  "mcpServers": {
    "thetabber": {
      "command": "npx",
      "args": ["-y", "@thetabber/mcp"],
      "env": { "TABBER_API_KEY": "ttbr_live_…" }
    }
  }
}`;

const CURL_SNIPPET = `curl https://thetabber.com/v1/posts \\
  -H "Authorization: Bearer ttbr_live_…" \\
  -d '{ "caption": "New drop 🎬",
        "social_accounts": ["acct_1","acct_2"],
        "media": ["media_9"] }'`;

const examplePrompts = [
  'Which social accounts do I have connected?',
  "Post this photo to Instagram and TikTok with the caption 'launch day'.",
  "Schedule 'new episode is out' to all my accounts for 9am Monday, New York time.",
  'Upload launch.mp4 and post it to every account right now.',
  'Did my last post go out everywhere? Show me any failures.',
  'Cancel the post I scheduled for Friday.',
];

const tools = [
  ['whoami', 'GET /v1/me', 'Confirm the key and workspace'],
  ['list_social_accounts', 'GET /v1/social-accounts', 'Fetch account UUIDs to post to'],
  ['create_post', 'POST /v1/posts', 'Publish now or schedule for later'],
  ['get_post · update_post', 'GET · PATCH /v1/posts/{id}', 'Read or edit a scheduled post'],
  ['cancel_post', 'DELETE /v1/posts/{id}', 'Cancel before it publishes'],
  ['upload_media', 'POST /v1/media/create-upload-url', 'Upload a file or URL → media UUID'],
  ['list_post_results', 'GET /v1/post-results', 'Per-account posted / failed outcomes'],
];

const faqs = [
  {
    q: 'What is the MCP server, exactly?',
    a: 'The Model Context Protocol is an open standard for wiring AI agents to tools. Our server exposes TheTabber as native tools, so agents like Claude and Cursor can post and schedule without you writing a line of HTTP.',
  },
  {
    q: 'Which agents work with it?',
    a: 'Anything that speaks MCP: Claude Desktop, Claude Code, Cursor, and Windsurf. Plus anything that can make an HTTP request through the REST API, like Codex, cron jobs, n8n, or your own scripts.',
  },
  {
    q: 'MCP, skill, or REST: which should I pick?',
    a: 'MCP gives the smoothest native tool-calling in Claude and Cursor. The skill teaches an agent the workflow and guardrails. REST is there when you want to call it directly from any language. All three hit the same endpoints.',
  },
  {
    q: 'How does authentication work?',
    a: 'Create a key in your dashboard (it looks like ttbr_live_…) and pass it as a bearer token, or set it as the TABBER_API_KEY environment variable for MCP.',
  },
  {
    q: 'Can the agent upload media?',
    a: 'Yes. Point it at a local file or a public URL and it requests a presigned upload, pushes the bytes, and attaches the media. One video or up to ten images per post.',
  },
  {
    q: 'What happens when a platform fails?',
    a: 'The others still go out. Each post returns per-account results, so the agent can read the failure reason and retry just the one that broke.',
  },
  {
    q: 'What does it cost?',
    a: 'Agent and API access is included with your TheTabber plan. Check the pricing page for the details.',
  },
];

export default function Agents() {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = (id: string, text: string) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden">
      <Header
        variant="static"
        isLoggedIn={false}
        navItems={[
          { label: 'Features', href: '/features' },
          { label: 'Pricing', href: '/pricing' },
          { label: 'Free Tools', href: '/tools' },
          { label: 'Agents', href: '/agents' },
          { label: 'Blog', href: '/blog' },
        ]}
      />

      {/* ── Hero: asymmetric copy + live conversation panel ────────────────── */}
      <section className="relative overflow-hidden border-b border-gray-100">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white" />
        <div className="absolute -top-32 right-0 w-[520px] h-[520px] bg-gray-100/70 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
            {/* Left: copy */}
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-6">
                Hand your social media
                <br />
                to an <span className="underline decoration-4 decoration-gray-300 underline-offset-4">agent</span>.
              </h1>

              <p className="text-lg sm:text-xl text-gray-600 max-w-xl mb-8 leading-relaxed">
                Connect TheTabber to Claude, Cursor, or Codex and let it draft, schedule, and
                publish across 9 platforms.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <button
                  type="button"
                  onClick={() => scrollTo('connect')}
                  className="group inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-7 py-3.5 rounded-xl text-base font-semibold hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl"
                >
                  Connect an agent
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <Link
                  to="/dashboard/settings/api-keys"
                  className="inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-900 px-7 py-3.5 rounded-xl text-base font-semibold hover:border-gray-400 hover:bg-gray-50 transition-all"
                >
                  Get an API key
                </Link>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-3">
                  Posts to
                </p>
                <TooltipProvider delayDuration={0}>
                  <div className="flex flex-wrap gap-3">
                    {platforms.map((platform, i) => (
                      <Tooltip key={i}>
                        <TooltipTrigger asChild>
                          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white border border-gray-200 hover:border-gray-400 hover:shadow-md transition-all cursor-default shadow-sm">
                            <platform.icon
                              className="text-xl"
                              style={{ color: platform.color }}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{platform.name}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </TooltipProvider>
              </div>
            </div>

            {/* Right: live agent conversation */}
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-tr from-gray-100 via-white to-transparent rounded-[2rem] blur-2xl" />
                <div className="relative rounded-2xl bg-white border border-gray-200 shadow-md overflow-hidden">
                  {/* Window chrome */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/80">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                  </div>
                  <div className="ml-2 text-xs font-medium text-gray-500">agent + thetabber</div>
                </div>

                {/* Messages */}
                <div className="p-4 sm:p-5 space-y-3 bg-gradient-to-b from-white to-gray-50/50">
                  <UserMsg>
                    Post &ldquo;New drop is live 🎬&rdquo; with{' '}
                    <code className="text-[13px] bg-white/15 px-1 py-0.5 rounded">launch.mp4</code>{' '}
                    to all my accounts.
                  </UserMsg>
                  <AgentMsg>Got it. Post it right now, or schedule it for later?</AgentMsg>
                  <UserMsg>Post it now.</UserMsg>
                  <ToolCall name="upload_media" result="launch.mp4" />
                  <ToolCall name="create_post" result="9 accounts · publishing now" />
                  <AgentMsg>Done, it&rsquo;s live on all 9 accounts. Want the links?</AgentMsg>
                </div>

                {/* Mock input bar */}
                {/* <div className="flex items-center gap-2 px-3 py-2.5 border-t border-gray-100 bg-white">
                  <div className="flex-1 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-400">
                    Ask your agent to post something…
                  </div>
                  <div className="w-8 h-8 shrink-0 rounded-lg bg-gray-900 flex items-center justify-center">
                    <PaperAirplaneIcon className="w-4 h-4 text-white" />
                  </div>
                </div> */}
                </div>
              </div>

              {/* Works-with row */}
              <div className="mt-6 relative flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-gray-500">
                <span>Works with</span>
                <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
                  <SiClaude className="w-4 h-4" />
                  Claude Code
                </span>
                <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
                  <CursorLogo className="w-4 h-4" />
                  Cursor
                </span>
                <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
                  <SiOpenai className="w-4 h-4" />
                  Codex
                </span>
                <span>or any agent that can make HTTP requests.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Three ways to connect (distinct cards) ─────────────────────────── */}
      <section id="connect" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Three ways to plug in
            </h2>
            <p className="text-lg text-gray-600">
              Same account, same endpoints. Start with whichever fits your setup, and mix them if you
              want.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* MCP */}
            <ConnectCard
              icon={CpuChipIcon}
              badge="Recommended"
              title="MCP server"
              body="Native tool-calling for Claude Desktop, Claude Code, and Cursor. One line to install."
            >
              <MiniCode id="mcp-cli" text={MCP_CLAUDE_CODE} copied={copied} onCopy={copy} />
              <TooltipProvider delayDuration={0}>
                <div className="flex items-center gap-2 mt-3">
                  {[
                    { Logo: SiClaude, name: 'Claude' },
                    { Logo: CursorLogo, name: 'Cursor' },
                    { Logo: SiOpenai, name: 'Codex' },
                  ].map(({ Logo, name }) => (
                    <Tooltip key={name}>
                      <TooltipTrigger asChild>
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 border border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-all cursor-default">
                          <Logo className="w-4 h-4" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{name}</p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                  <span className="text-sm text-gray-500">and others</span>
                </div>
              </TooltipProvider>
            </ConnectCard>

            {/* Skill */}
            <ConnectCard
              icon={PuzzlePieceIcon}
              title="Agent skill"
              body="A ready-made skill that teaches your agent the TheTabber workflow (find your accounts, upload media, post or schedule, confirm results) with guardrails. Pairs with MCP or the REST API."
            >
              <p className="text-sm text-gray-600">
                Add the <code className="text-gray-800">agent-mode</code> skill to Claude Code or
                your own agent, then set <code className="text-gray-800">TABBER_API_KEY</code>.
              </p>
              <a
                href="https://github.com/thetabber/mcp/tree/main/skills/agent-mode"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                View the skill
                <ArrowRightIcon className="w-3.5 h-3.5" />
              </a>
            </ConnectCard>

            {/* REST */}
            <ConnectCard
              icon={CodeBracketIcon}
              title="REST API"
              body="Any language, any framework. Bearer auth and a machine-readable OpenAPI 3 spec."
            >
              <MiniCode id="rest" text={CURL_SNIPPET} copied={copied} onCopy={copy} />
              <Link
                to="/docs/api"
                reloadDocument
                className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Read the docs
                <ArrowRightIcon className="w-3.5 h-3.5" />
              </Link>
            </ConnectCard>
          </div>
        </div>
      </section>

      {/* ── How it works: numbered flow ────────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-gray-900 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)',
            backgroundSize: '38px 38px',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-14 max-w-2xl">
            From a sentence to a post, in four steps
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { n: '1', t: 'Connect', d: 'Add the MCP server or your API key. The agent can now see your workspace.', link: '' },
              { n: '2', t: 'Ask', d: 'Tell it what to post, where, and when, in plain language.', link: 'examples' },
              { n: '3', t: 'Publish', d: 'It uploads media and fans the post out to every account you named.', link: '' },
              { n: '4', t: 'Verify', d: 'It reads back per-account results and retries anything that failed.', link: '' },
            ].map(({ n, t, d, link }) => (
              <div key={n} className="rounded-2xl border border-gray-800 bg-gray-800/40 p-6">
                <div className="w-10 h-10 rounded-xl bg-white text-gray-900 flex items-center justify-center text-lg font-black mb-4">
                  {n}
                </div>
                <h3 className="text-lg font-bold mb-2">{t}</h3>
                <p className="text-gray-400 leading-relaxed text-sm">{d}</p>
                {link && (
                  <button
                    type="button"
                    onClick={() => scrollTo(link)}
                    className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                  >
                    See example prompts
                    <ArrowRightIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Example prompts ─────────────────────────────────────────────────── */}
      <section id="examples" className="py-20 lg:py-28 bg-white scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Just say what you want
            </h2>
            <p className="text-lg text-gray-600">
              Talk to your agent in plain language. A few things people ask:
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {examplePrompts.map((p, i) => (
              <div
                key={p}
                className="relative rounded-2xl border border-gray-200 bg-gray-50 p-5 transition-all hover:shadow-md hover:border-gray-300"
              >
                <button
                  type="button"
                  onClick={() => copy(`prompt-${i}`, p)}
                  aria-label="Copy prompt"
                  className="absolute top-3 right-3 flex items-center gap-1 rounded-md bg-white border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-500 hover:text-gray-800 hover:border-gray-300 transition-colors"
                >
                  {copied === `prompt-${i}` ? (
                    <>
                      <CheckIcon className="w-3 h-3" /> Copied
                    </>
                  ) : (
                    <>
                      <ClipboardIcon className="w-3 h-3" /> Copy
                    </>
                  )}
                </button>
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-400 mb-3" />
                <p className="text-gray-800 leading-relaxed pr-2">&ldquo;{p}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tools reference ─────────────────────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Tool reference
              </h2>
            </div>
            <Link
              to="/docs/api"
              reloadDocument
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 whitespace-nowrap"
            >
              Full API docs
              <ArrowRightIcon className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <table className="w-full text-left">
              <tbody className="divide-y divide-gray-100 text-sm">
                {tools.map(([name, endpoint, desc]) => (
                  <tr key={name} className="hover:bg-gray-50">
                    <td className="px-5 py-3.5 font-mono text-gray-900 whitespace-nowrap">{name}</td>
                    <td className="px-5 py-3.5 font-mono text-gray-400 hidden sm:table-cell whitespace-nowrap">
                      {endpoint}
                    </td>
                    <td className="px-5 py-3.5 text-gray-600">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Plus <span className="font-mono text-gray-700">get_social_account</span>,{' '}
            <span className="font-mono text-gray-700">list_posts</span>,{' '}
            <span className="font-mono text-gray-700">list_media</span>,{' '}
            <span className="font-mono text-gray-700">get_media</span>,{' '}
            <span className="font-mono text-gray-700">delete_media</span>, and{' '}
            <span className="font-mono text-gray-700">get_post_result</span>.
          </p>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <FAQ
        items={faqs.map((f) => ({ question: f.q, answer: f.a }))}
        title="Some Questions, answered"
        align="left"
        className="py-20 lg:py-28 bg-white"
        showContactCta
      />

      {/* ── CTA ─────────────────────────────────────────────────────────────── */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gray-900 px-8 py-14 sm:px-14 text-center relative overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gray-700/30 rounded-full blur-[130px]" />
            <div className="relative">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Give your agent a voice on every platform
              </h2>
              <p className="text-lg text-gray-300 mb-8 max-w-xl mx-auto">
                Grab a key, connect the server, and let it start posting.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/dashboard/settings/api-keys"
                  className="group inline-flex items-center justify-center gap-2 bg-white text-gray-900 px-7 py-3.5 rounded-xl text-base font-semibold hover:bg-gray-100 transition-all"
                >
                  Get your API key
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/docs/api"
                  reloadDocument
                  className="inline-flex items-center justify-center gap-2 border border-gray-600 text-white px-7 py-3.5 rounded-xl text-base font-semibold hover:bg-gray-800 transition-all"
                >
                  Read the API docs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function UserMsg({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-gray-900 text-white px-3.5 py-2.5 text-sm leading-snug shadow-sm">
        {children}
      </div>
    </div>
  );
}

function AgentMsg({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-gray-100 text-gray-800 px-3.5 py-2.5 text-sm leading-snug">
        {children}
      </div>
    </div>
  );
}

function ToolCall({ name, result }: { name: string; result?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm">
      <div className="flex items-center gap-2 text-xs font-mono">
        <CommandLineIcon className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-gray-800">{name}</span>
      </div>
      {result && <div className="mt-1.5 pl-5 text-xs text-gray-500 font-mono">{result}</div>}
    </div>
  );
}

function ConnectCard({
  icon: Icon,
  badge,
  title,
  body,
  children,
}: {
  icon: typeof CpuChipIcon;
  badge?: string;
  title: string;
  body: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 hover:shadow-lg hover:border-gray-300 transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" />
        </div>
        {badge && (
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-600 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full">
            {badge}
          </span>
        )}
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 leading-relaxed mb-5 flex-1">{body}</p>
      {children}
    </div>
  );
}

function MiniCode({
  id,
  text,
  copied,
  onCopy,
}: {
  id: string;
  text: string;
  copied: string | null;
  onCopy: (id: string, text: string) => void;
}) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => onCopy(id, text)}
        aria-label="Copy to clipboard"
        title="Copy"
        className="absolute top-2.5 right-2.5 z-10 flex items-center gap-1 rounded-md bg-gray-800/80 border border-gray-700 px-2 py-1 text-[11px] font-mono text-gray-300 cursor-pointer hover:text-white hover:border-gray-600 transition-colors"
      >
        {copied === id ? (
          <>
            <CheckIcon className="w-3 h-3" /> Copied
          </>
        ) : (
          <>
            <ClipboardIcon className="w-3 h-3" /> Copy
          </>
        )}
      </button>
      <pre className="rounded-lg bg-gray-900 border border-gray-800 p-3.5 pr-16 text-[12.5px] leading-relaxed overflow-x-auto font-mono text-gray-300 whitespace-pre">
        <code>{text}</code>
      </pre>
    </div>
  );
}
